import {
  Component,
  ElementRef,
  ViewChild,
  signal,
  computed,
  OnInit,
  inject,
  DestroyRef,
} from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { CommonModule } from '@angular/common';
import { MsalService } from '@azure/msal-angular';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { SpinnerComponent } from '../../components/spinner/spinner';
import { NavButtonComponent } from '../../components/nav-button/nav-button';
import { ICountry } from '../../models/object.model';
import { FormsModule } from '@angular/forms';
// const environment = {
//   production: false,
//   apiUrl: 'http://localhost:4200',
//   msalPopup: true,
// };
import { CommonFormComponent, FormField } from '../../components/common-form/common-form';
import { CustomValidators } from '../../utilities/customer_validators';
import { ForgetPassword } from '../forget-password/forget-password';
import { forkJoin, from, switchMap, timer } from 'rxjs';
import { LP_UI_CONFIG } from '../../core/tokens';
import { I18nService } from '../../services/i18n.service';
import { LegalLocalizationService } from '../../services/legal-localization-service';
import { AuthenticationService } from '../../services/authentication.service';
import { AssetsService } from '../../services/asset.service';
import { SettingsService } from '../../services/settings.service';
import { UiService } from '../../services/ui.service';
import { UtilService } from '../../services/util.service';
import { FormKey } from '../../enums/i18.enum';
import { GLOBAL_CONSTANT } from '../../constants/global.constant';
import { StorageService } from '../../services/storage.service';
import { CookieBannerService } from '../../services/cookie-banner-service';
import { logError } from '../../utilities/global.util';
const ERROR_MESSAGE_LOGIN_DEACTIVATED = 'Login Deactivated';
const ERROR_MESSAGE_ROLE_NOT_AUTHORIZED = 'Role not authorized';
const ERROR_MESSAGE_PROMPT_SSO_CONVERSION_QUERY = 'Prompt SSO Conversion Query';
const ERROR_MESSAGE_IA_ACCESS_DENIED = 'IA access denied';
@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    NzImageModule,
    NzButtonModule,
    NzIconModule,
    NzGridModule,
    NzSpinModule,
    SpinnerComponent,
    NavButtonComponent,
    ForgetPassword,
    CommonFormComponent,
  ],
  templateUrl: './login.html',
  styleUrls: ['./login.scss'],
})
export class Login implements OnInit {
  // Inject services using inject()
  @ViewChild(CommonFormComponent)
  loginForm!: CommonFormComponent;
  public readonly cfg = inject(LP_UI_CONFIG);
  private router = inject(Router);
  public ui = inject(UiService);
  public i18n = inject(I18nService);
  public route = inject(ActivatedRoute);
  private lls = inject(LegalLocalizationService);
  private authService = inject(AuthenticationService);
  private assetService = inject(AssetsService);
  private settings = inject(SettingsService);
  private destroyRef = inject(DestroyRef);
  private util = inject(UtilService);
  private storage = inject(StorageService);
  private msalAuth = inject(MsalService);
  @ViewChild('emailInputRef') emailInputRef!: ElementRef;
  public cookieBannerService = inject(CookieBannerService);

  // Use signals for state management
  isLoading = signal(false);
  emailAlreadyRegistered = signal(false);
  error = signal(false);
  showAlert = signal(false);
  showForgetPassword = false;
  optional = signal(false);
  checkedTerms = signal(false);
  checkedServiceConditions = false;
  checkedDataProtection = signal(false);
  private queryParams = toSignal(this.route.queryParams);
  country = this.queryParams()?.['country'] ?? null;
  countryArray = signal<ICountry[]>([]);
  countrySelected = signal<any>(null);
  showNotUsPage = signal(true);
  sendMailError = false;
  acc_blocked = false;
  role_not_authorized = false;
  sso_conversion_query = false;
  temp_disposable_msal_login: any = null;
  isMobile = this.ui.IsMobile;
  isTablet = this.ui.IsTablet;
  isDesktop = this.ui.IsDesktop;
  returnUrl!: any;
  pwChanged!: boolean;
  signedUp!: boolean;
  hasSendPWForgotMail!: boolean;
  email: any;
  //Create you form  skeleton and pass to commonform
  loginFormConfig: FormField[] = [
    {
      controlName: FormKey.Username,
      label: this.i18n.langString(FormKey.Username),
      controlType: FormKey.Text,
      placeholder: this.i18n.langString(FormKey.Username),
      initialValue: null,
      validators: [...CustomValidators.requiredMinLength(FormKey.MinLength3)],
    },
    {
      controlName: FormKey.Password,
      label: this.i18n.langString(FormKey.Password),
      controlType: FormKey.Password,
      placeholder: this.i18n.langString(FormKey.Password),
      initialValue: null,
      validators: [Validators.required],
    },
    {
      controlName: FormKey.CaptchaResponse,
      label: this.cfg.enableCaptcha ? FormKey.Captcha : GLOBAL_CONSTANT.EMPTY_STRING,
      controlType: FormKey.Captcha,
      placeholder: '',
      initialValue: null,
      validators: this.cfg.enableCaptcha ? [Validators.required] : [],
    },
  ];
  temp_ssotoken = '';
  ia_access_denied = false;
  user = '';
  pw = '';

  get msalLogin(): boolean {
    return this.cfg.msalLogin;
  }

  ngOnInit(): void {
    const currentCountry = this.country;
    this.showNotUsPage.set(currentCountry !== 'us');
    let refAuth = null;

    this.route.params.subscribe((params) => {
      if (params['token']) {
        refAuth = params['token'];
        if (params['args']) {
          let redirectArgs = null;
          try {
            redirectArgs = JSON.parse(atob(params['args']));
          } catch (e) {
            redirectArgs = null;
            logError(e);
          }
          if (redirectArgs) this.returnUrl = redirectArgs.targetUrl + '/' + params['args'];
        }
      } else {
        if (params['key']) {
          const key = GLOBAL_CONSTANT.EMPTY_STRING + params['key'];
          if (key === 'pw-changed') this.pwChanged = true;
          if (key === 'signed-up') this.signedUp = true;
        }
        if (params['mail']) {
          this.toForgetPassword();
          this.hasSendPWForgotMail = false;
          this.email = params['mail'];
        }
      }
    });
    this.route.queryParams.subscribe((params) => {
      this.country = params['country'];
    });

    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/home';
    if (this.authService.returnUrl()) this.returnUrl = this.authService.returnUrl();

    // handle redirects from remote id provider
    if (this.route.snapshot.fragment) {
      const fragments = this.route.snapshot.fragment.split('&');
      const parameters: any = {};
      fragments.forEach((element) => {
        const pair = element.split('=');
        parameters[pair[0]] = pair[1];
      });

      if (this.cfg.debugAuth) parameters['debug_mode'] = 'true';
    }

    // Check login status with refAuth token if available
    if (this.authService.isLoggedIn() || !!refAuth) {
      this.isLoading.set(true);

      if (refAuth) {
        // Authenticate with refAuth token
        const decodedToken = decodeURIComponent(refAuth);
        this.authService
          .loginViaToken(decodedToken)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: async (success) => {
              this.isLoading.set(false);
              if (success) {
                // Call navigation API on first login with force reload
                try {
                  await this.assetService.getNavigation(true);
                } catch (error) {
                  logError(error);
                }
                this.router.navigate([this.returnUrl]);
              } else {
                this.authService.logout();
                this.isLoading.set(false);
              }
            },
            error: (error) => {
              this.isLoading.set(false);
              logError(error);
              this.authService.logout();
            },
          });
      } else if (this.authService.isLoggedIn()) {
        // User is already logged in, verify status
        Promise.all([
          this.settings.fetchUserSettings(),
          this.cfg.appName === 'em'
            ? this.assetService.getRegionsEM()
            : this.assetService.getRegions(),
          this.assetService.getNavigation(true),
        ])
          .then(() => {
            this.isLoading.set(false);
            this.router.navigate([this.returnUrl]);
          })
          .catch((error) => {
            this.isLoading.set(false);
            logError(error);
            this.authService.logout();
          });
      }
    } else {
      this.isLoading.set(false);
    }
  }

  private readonly allowedDomains = [
    new URL(this.cfg.prefix_equipment_monitor_uri).origin,
    new URL(this.cfg.prefix_installation_agent_uri).origin,
  ];

  private isAllowedExternalUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return this.allowedDomains.includes(parsed.origin);
    } catch {
      return false;
    }
  }

  toLoctitePulseapp(url: string): void {
    if (this.isAllowedExternalUrl(url)) {
      window.location.assign(url);
    } else {
      console.warn('Blocked invalid redirect URL');
    }
  }

  public EquipmentMonitorURL = computed(
    () =>
      this.cfg.prefix_equipment_monitor_uri +
      '/login/refauth/' +
      encodeURIComponent(this.storage.token())
  );

  public InstallationAgentURL = computed(
    () =>
      this.cfg.prefix_installation_agent_uri +
      '/login/refauth/' +
      encodeURIComponent(this.storage.token())
  );

  async loginViaMSAL() {
    console.log('LOGIN CLICKED');

    this.msalAuth.loginPopup().subscribe({
      next: (result: any) => {
        console.log(result);
        this.authorizeMsalLogin(result.idToken);
      },
      error: (error: any) => console.log(error),
    });
  }

  authorizeMsalLogin(access_token: string, convert = false) {
    this.onLoadingStarted();
    this.ia_access_denied = false;
    this.temp_ssotoken = access_token;
    this.temp_disposable_msal_login = this.authService
      .loginViaMsal(access_token, convert)
      .subscribe(
        () => {
          this.temp_ssotoken = '';
          forkJoin([
            this.assetService.getRegions(),
            // Not needed. The tab bar is always visible and fetches current notifications, 2023-02-06, LKlum
            // this.assets.getNotifications(),
            this.settings.getUserSettings(),
            timer(1500),
          ]).subscribe(() => {
            this.onLoadingFinished();
            this.user = '';
            this.pw = '';
            this.router.navigate([this.returnUrl]);
          });
        },
        (error) => {
          if (error.error !== ERROR_MESSAGE_PROMPT_SSO_CONVERSION_QUERY) this.temp_ssotoken = ''; // we only need this during the sso conversion prompt
          this.onLoadingFinished(error);
        }
      );
  }

  onLoadingStarted() {
    this.isLoading.set(true);
    this.error.set(false);
    this.util.setLoadingStarted(); // show splash screen for preloading plants and user settings after login
  }
  onLoadingFinished(error: any = false) {
    this.isLoading.set(false);
    if (error == null) {
      error = false;
    }
    if (
      error.error === ERROR_MESSAGE_ROLE_NOT_AUTHORIZED ||
      error.error === ERROR_MESSAGE_IA_ACCESS_DENIED
    ) {
      this.role_not_authorized = true;
      this.sso_conversion_query = false;
      this.acc_blocked = false;
      error = false;
    } else if (error.error == ERROR_MESSAGE_LOGIN_DEACTIVATED) {
      this.role_not_authorized = false;
      this.sso_conversion_query = false;
      this.acc_blocked = true;
      error = false;
    } else if (error.error == ERROR_MESSAGE_PROMPT_SSO_CONVERSION_QUERY) {
      this.role_not_authorized = false;
      this.acc_blocked = false;
      this.sso_conversion_query = true;
      error = false;
    } else {
      this.role_not_authorized = false;
      this.sso_conversion_query = false;
      this.acc_blocked = false;
      this.error.set(!!error);
    }
    this.util.setLoadingFinished(); // hide splash screen
  }

  login(formData: { username: string; password: string; captchaResponse: string }) {
    this.util.setLoadingStarted();
    // Convert observable to signal-based flow
    this.showAlert.set(false);
    this.error.set(false);
    if (
      formData.captchaResponse !== GLOBAL_CONSTANT.EMPTY_STRING &&
      formData.captchaResponse != null
    ) {
      const loginResult$ = this.authService
        .loginWithCredentials({
          user: formData.username,
          pw: formData.password,
          captchaResponse: formData.captchaResponse,
        })
        .pipe(
          switchMap(() => {
            return from(
              Promise.all([
                this.settings.fetchUserSettings(),
                this.cfg.appName === 'em'
                  ? this.assetService.getRegionsEM()
                  : this.assetService.getRegions(),
                this.assetService.getNavigation(true),
              ])
            );
          }),
          takeUntilDestroyed(this.destroyRef)
        );

      loginResult$.subscribe({
        next: () => {
          this.util.setLoadingFinished();
          this.router.navigate([this.returnUrl]);
        },
        error: (error: any) => {
          this.util.setLoadingFinished(false);
          this.error.set(true);
          this.showAlert.set(true);
          this.loginForm?.resetCaptcha();
          this.router.navigate([this.returnUrl]);
          logError(error);
        },
        complete: () => {
          this.util.setLoadingFinished();
        },
      });
    } else {
      const loginResult$ = this.authService
        .loginWithCredentials({
          user: formData.username,
          pw: formData.password,
        })
        .pipe(
          switchMap(() => {
            return from(
              Promise.all([
                this.settings.fetchUserSettings(),
                this.cfg.appName === 'em'
                  ? this.assetService.getRegionsEM()
                  : this.assetService.getRegions(),
                this.assetService.getNavigation(true),
              ])
            );
          }),
          takeUntilDestroyed(this.destroyRef)
        );

      loginResult$.subscribe({
        next: () => {
          this.util.setLoadingFinished();
          this.router.navigate([this.returnUrl]);
        },
        error: (_error: unknown) => {
          this.util.setLoadingFinished(false);
          this.error.set(true);
          this.showAlert.set(true);
          logError(this.returnUrl);
          this.router.navigate([this.returnUrl]);
        },
        complete: () => {
          this.util.setLoadingFinished();
        },
      });
    }
  }

  resetEmailAlreadyRegisterdcheck() {
    this.emailAlreadyRegistered.set(false);
  }

  toTermsOfUse() {
    if (this.country) {
      this.router.navigate(['us/terms-of-use']);
    } else {
      this.router.navigate(['/terms-of-use']);
    }
  }

  toForgetPassword() {
    this.showForgetPassword = true;
  }

  toCookieConsent() {
    this.router.navigate(['/cookie-consent']);
  }

  toLoginPage() {
    this.showForgetPassword = false;
  }

  toDataProtectionDeclaration() {
    if (this.country === 'us') {
      this.router.navigate(['us/data-protection']);
    } else {
      this.router.navigate(['/data-protection']);
    }
  }

  toServiceConditions() {
    this.router.navigate(['/service-conditions']);
  }

  toLegalImprint() {
    this.router.navigate(['/legal-imprint']);
  }

  toNoteForUsResidents() {
    this.router.navigate(['/note-for-us-residents']);
  }

  toprivacyPolicy() {
    window.location.href =
      'https://www.henkel-northamerica.com/privacy-statement-na?view=content-blank';
  }

  toCAPrivacyRights() {
    window.location.href = 'https://www.henkel-northamerica.com/ca-privacy-rights?pageID=564690';
  }

  toDSAR() {
    window.location.href =
      'https://www.henkel-northamerica.com/henkel-data-privacy-request?pageID=#pageID=2103458';
  }

  setCountry(id: any) {
    this.lls.signedUpCountry = id;
  }

  signupLogin() {
    if (this.country === 'us') {
      this.router.navigate(['/signup'], { queryParams: { country: 'us' } });
    } else {
      this.router.navigate(['/signup']);
    }
  }

  clearLoginError(): void {
    this.error.set(false);
    this.showAlert.set(false);
  }
}
